// oxlint-disable max-lines-per-function, complexity
import { mergeWith } from 'lodash-es';
import { assertEvent, assign, enqueueActions, sendParent } from 'xstate';

import { replaceKeys } from '#src/app/pages/startAPetitionV2/shared/utils';

import type { JobStartEvent } from '../../operations/types';
import { assertJobDone } from '../../utils';

import { machine } from './actor';

const replaceDecisionMakerResults = replaceKeys(['recommendations', 'selectedDecisionMakerIds', 'classification']);

/** lodash `merge` for this variant's draft updates, except that the decision-maker results are replaced. */
function merge<OBJ, SRC1, SRC2>(object: OBJ, source1: SRC1, source2: SRC2): OBJ & SRC1 & SRC2 {
	return mergeWith(object, source1, source2, replaceDecisionMakerResults);
}

/**
 * Provides the control machine's action and guard implementations, referenced by the state
 * chart in `./actor`. Kept separate from the chart so that external dependencies can be injected
 * into the implementation here.
 */
export const createLogic = () =>
	machine.provide({
		actions: {
			onSubmit: assign({
				draft: ({ context, event }) => {
					assertEvent(event, 'submit');

					return merge({}, context.draft, event.draft);
				},
			}),
			onPatch: assign({
				draft: ({ context, event }) => {
					assertEvent(event, 'patch');

					return merge({}, context.draft, event.draft);
				},
			}),
			// runs after onSubmit, so the draft already holds the issue prompt it infers from
			requestScopeLocation: sendParent(({ context }): JobStartEvent => ({
				type: 'job.start',
				name: 'inferScopeLocation',
				input: { mainPrompt: context.draft?.input?.issuePrompt ?? '' },
			})),
			applyScopeLocation: assign({
				draft: ({ context, event }) => {
					assertJobDone(event, 'inferScopeLocation');

					// keep the scope and place breakdown (CHANGE-97720, CHANGE-98274); drop confidences and reasoning
					const { output } = event;
					const inferredScopeLocation = output && {
						scope: output.scope,
						location: output.location.name,
						city: output.location.city,
						adminArea: output.location.adminArea,
						countryCode: output.location.countryCode,
					};

					return merge({}, context.draft, { metadata: { inferredScopeLocation } });
				},
			}),
			requestDecisionMakers: enqueueActions(({ context, enqueue }) => {
				const inferred = context.draft?.metadata?.inferredScopeLocation;
				const supportedCountries = context.draft?.metadata?.decisionMakers?.supportedTargetCountries ?? [];
				const eligible =
					inferred?.scope === 'LOCAL' &&
					!!inferred.city &&
					!!inferred.adminArea &&
					!!inferred.countryCode &&
					supportedCountries.includes(inferred.countryCode);

				enqueue.assign({
					draft: merge({}, context.draft, { metadata: { decisionMakers: { eligible } } }),
				});
				if (!eligible) return;

				enqueue.sendParent({
					type: 'job.start',
					name: 'inferDecisionMakers',
					input: {
						title: context.draft?.input?.issuePrompt ?? '',
						location: { city: inferred.city, adminArea: inferred.adminArea, countryCode: inferred.countryCode },
						draftId: context.draft?.id,
					},
				} satisfies JobStartEvent);
			}),
			applyDecisionMakers: assign({
				draft: ({ context, event }) => {
					assertJobDone(event, 'inferDecisionMakers');

					// already narrowed by the api; `null` when it settled without a result
					const recommendations = event.output?.decisionMakers ?? null;
					const classification = event.output?.classification ?? null;
					// everyone gets attached unless the starter removes them, whether or not they see the DM UI;
					// stays `null` when inference settled without a result, so publish can tell it never ran
					const selectedDecisionMakerIds =
						recommendations?.map(({ canonicalPetitionTargetId }) => canonicalPetitionTargetId) ?? null;

					return merge({}, context.draft, {
						metadata: { decisionMakers: { recommendations, classification } },
						petitionDraft: { selectedDecisionMakerIds },
					});
				},
			}),
			onError: assign({
				draft: ({ context, event }) => {
					assertEvent(event, 'error');

					const currentDraft = context.draft;

					const { issuePrompt = '', personalStory = '' } = currentDraft?.input ?? {};
					const description = `${issuePrompt} <br /><br />${personalStory}`;

					const userDraft = {
						type: 'user_draft',
						petitionDraft: { ask: null, description, image: null },
						recommendedDraft: null,
						editingDraft: null,
						metadata: { imageJobId: null },
					};

					const updatedDraft = merge({}, context.draft, userDraft);

					return updatedDraft;
				},
			}),
			unsetDraftType: assign({
				draft: ({ context, event }) => {
					assertEvent(event, 'back');

					const targetBranch = event.target; // target = null

					const updatedDraft = (() => {
						if (targetBranch === null) return merge({}, context.draft, { type: null });

						return context.draft;
					})();

					return updatedDraft;
				},
			}),
			persistDraftType: assign({
				draft: ({ context, event }) => {
					assertEvent(event, 'submit');

					const baseDraft = merge({}, context.draft, event.draft);

					const targetBranch = event.target; // 'ai' | 'user'

					const updatedDraft = (() => {
						if (targetBranch === null) return merge({}, baseDraft, { type: null });
						if (targetBranch === 'ai') return merge({}, baseDraft, { type: 'ai_draft' });
						if (targetBranch === 'user') return merge({}, baseDraft, { type: 'user_draft' });
						return baseDraft;
					})();

					return updatedDraft;
				},
			}),
		},
		guards: {
			isJobDone: ({ event }, { name }) => event.type === 'job.done' && event.name === name,
			shouldRequestScopeLocation: ({ context }) => !context.draft?.metadata?.inferredScopeLocation,
			transitionTo: ({ event }, params) => {
				assertEvent(event, 'submit');

				return event.target === params.target;
			},
			// -- branching and transition point
			isUserDraft: ({ context, event }) => {
				if (event.type === 'submit' && event.target) {
					return event.target === 'user';
				}
				return context.draft?.type === 'user_draft';
			},
			isAiDraft: ({ context, event }) => {
				if (event.type === 'submit' && event.target) {
					return event.target === 'ai';
				}
				return context.draft?.type === 'ai_draft';
			},
		},
	});
